/**
 * THE-HOME-LOADS-FAST-2 — THE REPORTING SEAM, AND THE BUFFER BEHIND IT.
 *
 * ─── WHY THERE IS A SEAM AT ALL ────────────────────────────────────────────
 *
 * THE-HOME-LOADS-FAST-1 measured the dashboard home's first load and costed
 * what was left: **about 440 KB of source in the entry chunk was Sentry**
 * (`@sentry/core`, `@sentry/browser`, `@sentry-internal/browser-utils`),
 * downloaded and executed before a merchant's first screen drew a word. It was
 * at the top of `main.tsx` on purpose, so that an error during boot is caught,
 * and that purpose is real: the window where the app is starting is exactly
 * where the errors nobody can reproduce happen.
 *
 * Ruled: error reporting STAYS, and loads after the home paints. A tiny early
 * handler holds what happens in between and hands it over once the reporter is
 * there. This module is that handler, and it is the only thing the app imports:
 * five call sites that each imported `@sentry/react` now import this instead,
 * so there is one seam rather than five paths into the same 440 KB.
 *
 * ─── WHAT IT PROMISES ──────────────────────────────────────────────────────
 *
 * An error thrown before the reporter loads is delivered once it loads. Not
 * "usually": the buffer is bounded and the bound is the ONLY thing that drops
 * anything, and it drops the NEWEST rather than the oldest, because the first
 * error in a boot is the one that explains the rest and the hundredth is
 * almost always its echo.
 *
 * A REPORTING FAILURE MUST NEVER BECOME AN ERROR OF ITS OWN. Every path here
 * either holds a value or hands it on; nothing throws, so a caller already
 * handling a failure is never handed a second one.
 *
 * PURE, AND A LEAF: no imports, so Stryker reaches every branch.
 */

/** The shape a call site passes, which is the shape Sentry already took. */
export interface ReportContext {
  tags?: Record<string, string | undefined>
  extra?: Record<string, unknown>
}

/** Who is signed in, or null for nobody. */
export type ReportUser = { id: string; email?: string } | null

/** What the real reporter must answer to, once it is loaded. */
export interface Reporter {
  captureException(error: unknown, context?: ReportContext): void
  setUser(user: ReportUser): void
}

/**
 * HOW MANY ERRORS ARE HELD. A boot that is failing can throw in a loop, and a
 * buffer with no ceiling turns one bug into a memory leak on a phone.
 */
export const HELD_CEILING = 50

interface Held {
  error: unknown
  context?: ReportContext
}

let live: Reporter | null = null
let held: Held[] = []
let dropped = 0
/** `undefined` means nobody has said yet; `null` means nobody is signed in. */
let heldUser: ReportUser | undefined

/**
 * REPORT AN ERROR, whether or not the reporter is here yet.
 *
 * Before it is here, the error is held. After, it goes straight through. The
 * call site cannot tell the difference and is not asked to.
 */
export function captureException(error: unknown, context?: ReportContext): void {
  if (live !== null) {
    try {
      live.captureException(error, context)
    } catch {
      // The reporter itself failed. Nothing here may throw.
    }
    return
  }
  if (held.length >= HELD_CEILING) {
    // THE NEWEST IS DROPPED, NOT THE OLDEST. The first error in a failing boot
    // is the one that explains the others.
    dropped += 1
    return
  }
  held.push({ error, context })
}

/** Who is signed in. Held the same way, and only the latest answer matters. */
export function setUser(user: ReportUser): void {
  if (live !== null) {
    try {
      live.setUser(user)
    } catch {
      // As above.
    }
    return
  }
  heldUser = user
}

/**
 * THE REPORTER HAS ARRIVED. Everything held goes over, in the order it
 * happened, and the count of anything the ceiling dropped goes with it so a
 * reader is never told a quiet boot was quiet when it was not.
 */
export function handOver(reporter: Reporter): void {
  live = reporter
  const queue = held
  const lost = dropped
  held = []
  dropped = 0
  try {
    if (heldUser !== undefined) reporter.setUser(heldUser)
    for (const one of queue) reporter.captureException(one.error, one.context)
    if (lost > 0) {
      reporter.captureException(new Error('errors were dropped before reporting began'), {
        tags: { boundary: 'report', boundary_kind: 'held_overflow' },
        extra: { dropped: lost, ceiling: HELD_CEILING },
      })
    }
  } catch {
    // A reporter that throws on its first use is not worth a crash.
  }
  heldUser = undefined
}

/** How many errors are waiting. For the guard, so it can see the holding. */
export function heldCount(): number {
  return held.length
}

/** How many the ceiling refused. For the guard, and for the handover. */
export function droppedCount(): number {
  return dropped
}

/** Put it back as it started. For tests, so one does not reach the next. */
export function forgetHeld(): void {
  live = null
  held = []
  dropped = 0
  heldUser = undefined
}

/**
 * WATCH FOR WHAT HAPPENS BEFORE THE REPORTER LOADS.
 *
 * Two listeners and nothing else, registered on the first line the app runs, so
 * the window this whole wave opens — between the page starting and the reporter
 * arriving — is covered rather than merely apologised for.
 *
 * They do NOT preventDefault and they do NOT stop propagation: the browser's own
 * console output and every other listener are exactly as they were.
 */
export function watchForEarlyErrors(win: {
  addEventListener(type: string, listener: (event: unknown) => void): void
}): () => void {
  const onError = (event: unknown) => {
    const e = event as { error?: unknown; message?: unknown } | null
    captureException(e?.error ?? new Error(String(e?.message ?? 'error before reporting began')), {
      tags: { boundary: 'early', boundary_kind: 'error' },
    })
  }
  const onRejection = (event: unknown) => {
    const e = event as { reason?: unknown } | null
    captureException(e?.reason ?? new Error('rejection before reporting began'), {
      tags: { boundary: 'early', boundary_kind: 'unhandledrejection' },
    })
  }
  win.addEventListener('error', onError)
  win.addEventListener('unhandledrejection', onRejection)
  return () => {
    const off = win as unknown as {
      removeEventListener?: (t: string, l: (e: unknown) => void) => void
    }
    off.removeEventListener?.('error', onError)
    off.removeEventListener?.('unhandledrejection', onRejection)
  }
}
